import { Fragment, useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import {
  FiChevronRight,
  FiChevronDown,
  FiRefreshCw,
  FiPlus,
  FiEye,
  FiEyeOff,
  FiTrash2,
  FiFileText,
} from "react-icons/fi";
import { useFetchVendorsQuery } from "../../../features/admin/vendor/vendorApi";
import { useFetchFormTemplateMutation } from "../../../features/admin/formTemplate/formTemplateApi";
import {
  useAddFormSetupMutation,
  useDeleteFormSetupMutation,
  useFetchFormSetupMutation,
} from "../../../features/admin/formSetup/formSetupApi";
import {
  Page,
  PageHeader,
  Button,
  SearchInput,
  Table,
  THead,
  Th,
  Td,
  EmptyRow,
  Badge,
  StatusDot,
  Tag,
  Chip,
  Modal,
  Drawer,
  Spinner,
} from "../../../components/ui";

// 12 form types (mirrors FromSetup.jsx)
const FORM_TYPES = [
  { id: 1, name: "Payment" },
  { id: 2, name: "Warranty" },
  { id: 3, name: "KYC" },
  { id: 4, name: "Login" },
  { id: 5, name: "Registration" },
  { id: 6, name: "Profile" },
  { id: 7, name: "Panel-AddUser" },
  { id: 8, name: "App-AddUser" },
  { id: 9, name: "Enquiry" },
  { id: 10, name: "WheelCampaign" },
  { id: 11, name: "App-Feedback" },
  { id: 12, name: "Report-Qr" },
];

const getTypeName = (id) => FORM_TYPES.find((t) => t.id === Number(id))?.name || `Type ${id}`;

const token = () => sessionStorage.getItem("access_token");

// ── Shared: template field list preview ────────────────────────────────────
const FieldList = ({ template }) => {
  const fields = Object.values(template?.template || {});
  if (!fields.length)
    return <p className="text-sm text-gray-400 text-center py-6">No fields in this template.</p>;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {fields.map((field, idx) => (
        <div key={idx} className="rounded-lg border border-gray-200 bg-white px-3 py-2">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-medium text-gray-800 truncate">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </span>
            <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 shrink-0">
              {field.type}
            </span>
          </div>
          {Array.isArray(field.options) && field.options.length > 0 && (
            <div className="mt-1 text-xs text-gray-400 truncate">{field.options.join(", ")}</div>
          )}
        </div>
      ))}
    </div>
  );
};

// ── Professional, monochrome form tag ──────────────────────────────────────
const FormTag = ({ form, onView, onRemove }) => (
  <Tag
    label={getTypeName(form.form_template_type)}
    sub={form.form_template_name}
    title={form.form_template_name}
    actions={
      <>
        <button
          type="button"
          onClick={onView}
          title="Preview form"
          className="p-1 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
        >
          <FiEye className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={onRemove}
          title="Remove form"
          className="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
        >
          <FiTrash2 className="h-3.5 w-3.5" />
        </button>
      </>
    }
  />
);

const FormSetupByClient = () => {
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState({});
  const [assign, setAssign] = useState(null); // { tenant, userType, remaining }
  const [viewer, setViewer] = useState(null); // template object

  const { data: vendorsData, isFetching: vendorsFetching } =
    useFetchVendorsQuery(token(), { refetchOnMountOrArgChange: true }) || {};
  const [fetchUsage, { data: usageData, isLoading: usageLoading }] = useFetchFormSetupMutation();
  const [fetchTemplates, { data: templateData }] = useFetchFormTemplateMutation();
  const [addFormSetup, { isLoading: adding }] = useAddFormSetupMutation();
  const [deleteFormSetup] = useDeleteFormSetupMutation();

  const load = () => {
    fetchUsage(token());
    fetchTemplates(token());
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const templates = templateData?.body || [];

  // ── group tenant-wise → user_types → assigned form usages ────────────────
  const tenants = useMemo(() => {
    const vendors = vendorsData?.body || [];
    const usages = usageData?.body || [];
    const map = new Map();

    const ensureTenant = (tenant_id, name) => {
      if (!tenant_id && tenant_id !== 0) return null;
      if (!map.has(tenant_id)) {
        map.set(tenant_id, { tenant_id, tenant_name: name || `Tenant ${tenant_id}`, userTypes: new Map() });
      }
      const t = map.get(tenant_id);
      if (name && (!t.tenant_name || t.tenant_name.startsWith("Tenant "))) t.tenant_name = name;
      return t;
    };
    const ensureUT = (t, ut) => {
      if (!t.userTypes.has(ut)) t.userTypes.set(ut, { user_type: ut, forms: [] });
      return t.userTypes.get(ut);
    };

    vendors.forEach((v) => {
      if (!v.tenant_id && v.tenant_id !== 0) return;
      const t = ensureTenant(v.tenant_id, v.name);
      (v.user_types || []).forEach((ut) => ensureUT(t, ut));
    });
    usages.forEach((row) => {
      const t = ensureTenant(row.tenant_id, row.tenant_name);
      if (!t) return;
      ensureUT(t, row.user_type).forms.push(row);
    });

    return Array.from(map.values())
      .map((t) => {
        const userTypes = Array.from(t.userTypes.values())
          .map((u) => ({
            ...u,
            forms: [...u.forms].sort((a, b) => a.form_template_type - b.form_template_type),
          }))
          .sort((a, b) => a.user_type.localeCompare(b.user_type));
        return {
          ...t,
          userTypes,
          formCount: userTypes.reduce((n, u) => n + u.forms.length, 0),
          emptyCount: userTypes.filter((u) => u.forms.length === 0).length,
        };
      })
      .sort((a, b) => (a.tenant_name || "").localeCompare(b.tenant_name || ""));
  }, [vendorsData, usageData]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tenants;
    return tenants.filter(
      (t) => (t.tenant_name || "").toLowerCase().includes(q) || String(t.tenant_id).includes(q),
    );
  }, [tenants, search]);

  const loading = usageLoading || vendorsFetching;

  // ── actions ──────────────────────────────────────────────────────────────
  const openAssign = (tenant, u) => {
    const assignedTypes = new Set(u.forms.map((f) => Number(f.form_template_type)));
    const remaining = FORM_TYPES.filter((t) => !assignedTypes.has(t.id));
    setAssign({
      tenant: { tenant_id: tenant.tenant_id, tenant_name: tenant.tenant_name },
      userType: u.user_type,
      remaining,
    });
  };

  const openViewer = (row) => {
    const tpl = templates.find((f) => f.id === row.form_template_id);
    if (tpl) setViewer(tpl);
    else toast.error("Template data not available.");
  };

  const confirmDelete = (row) => {
    toast(
      (t) => (
        <span>
          <p className="font-semibold text-gray-800">Remove this form?</p>
          <p className="text-xs text-gray-500 mt-0.5">
            {getTypeName(row.form_template_type)} · {row.form_template_name}
          </p>
          <div className="flex gap-2 mt-3">
            <button
              className="px-3 py-1.5 text-xs rounded-lg bg-red-600 text-white hover:bg-red-700 font-medium"
              onClick={async () => {
                toast.dismiss(t.id);
                try {
                  await deleteFormSetup({ token: token(), id: row.id }).unwrap();
                  toast.success("Removed");
                  fetchUsage(token());
                } catch {
                  toast.error("Delete failed");
                }
              }}
            >
              Remove
            </button>
            <button
              className="px-3 py-1.5 text-xs rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium"
              onClick={() => toast.dismiss(t.id)}
            >
              Cancel
            </button>
          </div>
        </span>
      ),
      { id: "deleteFormSetup", duration: Infinity },
    );
  };

  const coverage = (t) => {
    if (t.userTypes.length === 0) return <StatusDot tone="gray" label="No user types" />;
    if (t.emptyCount > 0)
      return (
        <StatusDot
          tone="amber"
          label={`${t.emptyCount} user type${t.emptyCount === 1 ? "" : "s"} with no forms`}
        />
      );
    return <StatusDot tone="green" label="All user types have forms" />;
  };

  return (
    <Page>
      <PageHeader
        title="Form Setup by Client"
        subtitle="Expand a client to see each user type's assigned form types. “Add” only offers the form types not yet set up."
        actions={
          <Button
            variant="outline"
            icon={<FiRefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />}
            onClick={load}
          >
            Refresh
          </Button>
        }
      />

      <SearchInput
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search clients by name or ID…"
        className="mb-3"
      />

      <Table>
        <THead>
          <tr>
            <th className="w-12 px-3 py-2.5" />
            <Th>Client</Th>
            <Th>User types</Th>
            <Th>Forms set up</Th>
            <Th>Coverage</Th>
          </tr>
        </THead>
        <tbody>
          {loading && filtered.length === 0 && <EmptyRow colSpan={5}>Loading clients…</EmptyRow>}
          {!loading && filtered.length === 0 && <EmptyRow colSpan={5}>No clients found.</EmptyRow>}

          {filtered.map((tenant) => {
            const isOpen = !!expanded[tenant.tenant_id];
            return (
              <Fragment key={tenant.tenant_id}>
                <tr
                  onClick={() =>
                    setExpanded((prev) => ({ ...prev, [tenant.tenant_id]: !prev[tenant.tenant_id] }))
                  }
                  className={`border-t border-gray-100 cursor-pointer transition-colors ${
                    isOpen ? "bg-blue-50/40" : "hover:bg-gray-50/70"
                  }`}
                >
                  <td className="px-3 py-2.5 text-center align-middle">
                    <FiChevronRight
                      className={`inline h-4 w-4 text-gray-400 transition-transform ${
                        isOpen ? "rotate-90" : ""
                      }`}
                    />
                  </td>
                  <Td>
                    <span className="font-semibold text-gray-900">{tenant.tenant_name}</span>
                    <span className="ml-2 text-xs font-normal text-gray-400">ID: {tenant.tenant_id}</span>
                  </Td>
                  <Td className="text-gray-600">{tenant.userTypes.length}</Td>
                  <Td className="text-gray-600">
                    <span className="inline-flex items-center gap-1.5">
                      <FiFileText className="h-4 w-4 text-gray-400" />
                      {tenant.formCount}
                    </span>
                  </Td>
                  <Td>{coverage(tenant)}</Td>
                </tr>

                {isOpen && (
                  <tr className="border-t border-gray-100 bg-gray-50/50">
                    <td colSpan={5} className="px-4 md:px-6 py-5">
                      {tenant.userTypes.length === 0 ? (
                        <p className="text-sm text-gray-400 text-center py-6">
                          No user types set up for this client.
                        </p>
                      ) : (
                        <Table>
                          <THead>
                            <tr>
                              <Th className="w-48">User type</Th>
                              <Th>Form types set up</Th>
                              <th className="px-4 py-2.5 w-28" />
                            </tr>
                          </THead>
                          <tbody>
                            {tenant.userTypes.map((u) => (
                              <tr key={u.user_type} className="border-t border-gray-100">
                                <Td>
                                  <Badge tone="indigo" className="capitalize">
                                    {u.user_type}
                                  </Badge>
                                </Td>
                                <Td>
                                  {u.forms.length === 0 ? (
                                    <span className="text-xs text-gray-400 italic">
                                      No forms set up yet
                                    </span>
                                  ) : (
                                    <div className="flex flex-wrap gap-2">
                                      {u.forms.map((f) => (
                                        <FormTag
                                          key={f.id}
                                          form={f}
                                          onView={() => openViewer(f)}
                                          onRemove={() => confirmDelete(f)}
                                        />
                                      ))}
                                    </div>
                                  )}
                                </Td>
                                <Td className="text-right">
                                  <Button
                                    size="sm"
                                    variant="soft"
                                    icon={<FiPlus className="h-3.5 w-3.5" />}
                                    onClick={() => openAssign(tenant, u)}
                                  >
                                    Add
                                  </Button>
                                </Td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </Table>

      {/* assign drawer */}
      {assign && (
        <AssignFormDrawer
          assign={assign}
          templates={templates}
          adding={adding}
          onClose={() => setAssign(null)}
          onSubmit={async (formData, formTypeId) => {
            try {
              await addFormSetup({
                token: token(),
                body: {
                  tenant_id: assign.tenant.tenant_id,
                  tenant_name: assign.tenant.tenant_name,
                  industry_id: formData.industry_id,
                  industry_name: formData.industry_name,
                  form_template_id: formData.id,
                  form_template_name: formData.template_name,
                  form_template_type: Number(formTypeId),
                  user_type: assign.userType,
                },
              }).unwrap();
              toast.success("Form assigned");
              setAssign(null);
              fetchUsage(token());
            } catch (err) {
              toast.error(err?.data?.message || "Assignment failed");
            }
          }}
        />
      )}

      {/* template preview modal */}
      {viewer && <TemplatePreviewModal template={viewer} onClose={() => setViewer(null)} />}
    </Page>
  );
};

// ── Assign drawer: pick a remaining form type, then a template of that type ──
const AssignFormDrawer = ({ assign, templates, adding, onClose, onSubmit }) => {
  const [typeId, setTypeId] = useState("");
  const [formId, setFormId] = useState("");
  const [showPreview, setShowPreview] = useState(false);

  const formsOfType = useMemo(
    () => templates.filter((f) => String(f.form_type) === String(typeId)),
    [templates, typeId],
  );
  const selectedForm = useMemo(
    () => formsOfType.find((f) => String(f.id) === String(formId)),
    [formsOfType, formId],
  );

  const submit = () => {
    if (!typeId) return toast.error("Pick a form type");
    if (!selectedForm) return toast.error("Pick a form");
    onSubmit(selectedForm, typeId);
  };

  return (
    <Drawer
      width="md"
      onClose={onClose}
      title="Add a form"
      chips={[
        { label: assign.tenant.tenant_name, tone: "blue" },
        { label: <span className="capitalize">{assign.userType}</span>, tone: "gray" },
      ]}
      footer={
        <>
          <span className="text-xs text-gray-500 truncate">
            {selectedForm
              ? `${getTypeName(typeId)} · ${selectedForm.template_name}`
              : "Pick a form type & template"}
          </span>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={submit}
              disabled={adding || !selectedForm}
              icon={adding ? <Spinner /> : null}
            >
              Assign form
            </Button>
          </div>
        </>
      }
    >
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
        {/* remaining form types */}
        <div>
          <p className="text-sm font-medium text-gray-700 mb-2">
            Form type <span className="text-gray-400 font-normal">· only types not yet added</span>
          </p>
          {assign.remaining.length === 0 ? (
            <div className="rounded-xl border border-emerald-100 bg-emerald-50/60 px-4 py-3 text-sm text-emerald-700">
              All 12 form types are already set up for this user type.
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {assign.remaining.map((t) => (
                <Chip
                  key={t.id}
                  active={String(typeId) === String(t.id)}
                  onClick={() => {
                    setTypeId(t.id);
                    setFormId("");
                    setShowPreview(false);
                  }}
                >
                  {t.name}
                </Chip>
              ))}
            </div>
          )}
        </div>

        {/* templates of the chosen type */}
        {typeId && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-gray-700">Choose a form template</p>
              {selectedForm && (
                <button
                  type="button"
                  onClick={() => setShowPreview((p) => !p)}
                  className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-md transition-colors ${
                    showPreview
                      ? "bg-blue-100 text-blue-700 hover:bg-blue-200"
                      : "text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                  }`}
                >
                  {showPreview ? <FiEyeOff className="h-3.5 w-3.5" /> : <FiEye className="h-3.5 w-3.5" />}
                  {showPreview ? "Hide preview" : "Quick view"}
                </button>
              )}
            </div>
            {formsOfType.length === 0 ? (
              <p className="text-xs text-amber-600">
                No templates exist for “{getTypeName(typeId)}”. Create one under Form Template first.
              </p>
            ) : (
              <div className="relative">
                <select
                  value={formId}
                  onChange={(e) => {
                    setFormId(e.target.value);
                    setShowPreview(false);
                  }}
                  className="block w-full pl-3 pr-9 py-2.5 border border-gray-300 rounded-lg bg-white text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition-all"
                >
                  <option value="">Select form…</option>
                  {formsOfType.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.template_name} · {f.industry_name} · {f.language}
                    </option>
                  ))}
                </select>
                <FiChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              </div>
            )}

            {/* inline quick view */}
            {selectedForm && showPreview && (
              <div className="mt-3 rounded-xl border border-gray-200 bg-gray-50/70 p-3">
                <p className="text-xs text-gray-500 mb-2.5">
                  {Object.keys(selectedForm.template || {}).length} field
                  {Object.keys(selectedForm.template || {}).length === 1 ? "" : "s"} ·{" "}
                  {selectedForm.language} · {selectedForm.industry_name}
                </p>
                <div className="max-h-56 overflow-y-auto pr-1">
                  <FieldList template={selectedForm} />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Drawer>
  );
};

// ── Standalone template preview modal (from a tag) ─────────────────────────
const TemplatePreviewModal = ({ template, onClose }) => {
  const fieldCount = Object.keys(template.template || {}).length;
  return (
    <Modal
      size="lg"
      onClose={onClose}
      title={template.template_name}
      subtitle={`${getTypeName(template.form_type)} · ${template.industry_name} · ${template.language} · ${fieldCount} field${
        fieldCount === 1 ? "" : "s"
      }`}
    >
      <FieldList template={template} />
    </Modal>
  );
};

export default FormSetupByClient;
